import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiClient from '@/api/client';
import { getApiErrorMessage } from '@/lib/utils';

type Step = 'username' | 'otp';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('username');
  const [username, setUsername] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleUsernameSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!username.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await apiClient.post('/auth/forgot-password', {
        username: username.trim(),
      });
      setStep('otp');
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifySubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!otp.trim() || !newPassword || !username.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await apiClient.post('/auth/verify-otp', {
        username: username.trim(),
        otp: otp.trim(),
        newPassword,
      });
      navigate('/login', { replace: true });
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  function handleBackToUsername() {
    setStep('username');
    setOtp('');
    setNewPassword('');
    setError(null);
  }

  // Step 2: OTP + New Password
  if (step === 'otp') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-full max-w-sm p-8 bg-card rounded-lg shadow">
          <h1 className="text-xl font-bold mb-1">Reset Password</h1>
          <p className="text-sm text-muted-foreground mb-1">Tetapkan semula kata laluan</p>
          <p className="text-xs text-muted-foreground mb-5">
            We sent a 6-digit code to your registered phone number.
          </p>

          <form onSubmit={handleVerifySubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                OTP Code
                <span className="text-muted-foreground font-normal text-xs ml-1">
                  Kod pengesahan
                </span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-full border rounded px-3 py-3 text-center text-lg font-mono tracking-widest focus:outline-none focus:ring-1 focus:ring-ring"
                autoComplete="one-time-code"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                New Password
                <span className="text-muted-foreground font-normal text-xs ml-1">
                  Kata laluan baharu
                </span>
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                autoComplete="new-password"
                minLength={8}
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Minimum 8 characters / Minimum 8 aksara
              </p>
            </div>

            {error && <p className="text-destructive text-sm">{error}</p>}

            <button
              type="submit"
              disabled={loading || otp.length < 6 || newPassword.length < 8}
              className="w-full bg-primary text-primary-foreground py-2 rounded text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? 'Verifying...' : 'Reset Password'}
            </button>

            <button
              type="button"
              onClick={handleBackToUsername}
              className="w-full text-sm text-muted-foreground hover:text-foreground"
            >
              Back
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Step 1: Username Entry
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-sm p-8 bg-card rounded-lg shadow">
        <h1 className="text-xl font-bold mb-1">Forgot Password</h1>
        <p className="text-sm text-muted-foreground mb-1">Lupa kata laluan</p>
        <p className="text-xs text-muted-foreground mb-5">
          Enter your username to receive a verification code.
        </p>

        <form onSubmit={handleUsernameSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Username
              <span className="text-muted-foreground font-normal text-xs ml-1">Nama pengguna</span>
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              autoComplete="username"
              required
            />
          </div>

          {error && <p className="text-destructive text-sm">{error}</p>}

          <button
            type="submit"
            disabled={loading || !username.trim()}
            className="w-full bg-primary text-primary-foreground py-2 rounded text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? 'Sending...' : 'Send Code'}
          </button>
        </form>

        <p className="text-center text-sm text-muted-foreground mt-4">
          Remember your password?{' '}
          <Link to="/login" className="text-primary hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
